import { useEffect, useRef, useState } from "react";

import {
  encode,
  patternToTimeline,
  play,
  type Playback,
  type Segment,
  totalDurationMs,
} from "@nav-games/morse";

/// Type a message, hear it played as Morse, watch the lamp flash in
/// sync with the audio. Pure-client — no auth required.

export function MorseDemo() {
  const [text, setText] = useState("SOS");
  const [wpm, setWpm] = useState(20);
  const [lampOn, setLampOn] = useState(false);
  const [playing, setPlaying] = useState(false);
  const playbackRef = useRef<Playback | null>(null);
  const lampTimeoutsRef = useRef<number[]>([]);

  // Cleanup any in-flight playback when the component unmounts (e.g.
  // navigation to another page mid-tone) so the audio doesn't ghost.
  useEffect(() => {
    return () => {
      playbackRef.current?.stop();
      for (const id of lampTimeoutsRef.current) clearTimeout(id);
    };
  }, []);

  const morse = encode(text);
  const timeline = patternToTimeline(morse, wpm);
  const totalMs = totalDurationMs(timeline);

  const start = () => {
    playbackRef.current?.stop();
    for (const id of lampTimeoutsRef.current) clearTimeout(id);
    lampTimeoutsRef.current = [];

    setPlaying(true);
    setLampOn(false);
    const handle = play(timeline);
    playbackRef.current = handle;

    // Drive the lamp from the same timeline. JS setTimeout isn't
    // perfectly aligned to the audio scheduler but at 60ms/unit (20
    // WPM) the drift is invisible.
    let cursor = 0;
    for (const seg of timeline) {
      const startAt = cursor;
      const endAt = cursor + seg.durationMs;
      lampTimeoutsRef.current.push(
        window.setTimeout(() => setLampOn(seg.on), startAt),
      );
      lampTimeoutsRef.current.push(
        window.setTimeout(() => setLampOn(false), endAt),
      );
      cursor = endAt;
    }
    void handle.done.finally(() => {
      setPlaying(false);
      setLampOn(false);
      playbackRef.current = null;
    });
  };

  const stop = () => {
    playbackRef.current?.stop();
    for (const id of lampTimeoutsRef.current) clearTimeout(id);
    lampTimeoutsRef.current = [];
    setPlaying(false);
    setLampOn(false);
  };

  return (
    <div className="card">
      <h2>Morse</h2>
      <label className="small muted" htmlFor="morse-input">
        Message
      </label>
      <input
        id="morse-input"
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type a message — letters, digits, punctuation"
        style={{ marginTop: 4, marginBottom: 12 }}
      />
      <div className="row" style={{ marginBottom: 12 }}>
        <label className="small muted" htmlFor="wpm">
          Speed (WPM)
        </label>
        <input
          id="wpm"
          type="number"
          min={5}
          max={40}
          value={wpm}
          onChange={(e) => setWpm(Math.max(1, Number(e.target.value) || 20))}
          style={{ width: 80 }}
        />
        <span className="small muted">
          ≈ {Math.round(totalMs / 100) / 10}s to play
        </span>
      </div>
      <div className="row" style={{ marginBottom: 16 }}>
        {playing ? (
          <button className="secondary" onClick={stop}>
            Stop
          </button>
        ) : (
          <button onClick={start} disabled={text.trim().length === 0}>
            Play
          </button>
        )}
      </div>
      <div className="row">
        <div className={`lamp${lampOn ? " on" : ""}`} aria-hidden="true" />
        <pre style={{ margin: 0, fontFamily: "ui-monospace, monospace", fontSize: 13, color: "var(--text-muted)" }}>
          {morse || <em>(empty)</em>}
        </pre>
      </div>
      <TimelineRibbon timeline={timeline} totalMs={totalMs} />
    </div>
  );
}

/// Read-only strip showing the on/off pattern as a tiny waveform —
/// helpful for visually checking timing without playing audio.
function TimelineRibbon({ timeline, totalMs }: { timeline: readonly Segment[]; totalMs: number }) {
  if (timeline.length === 0 || totalMs === 0) return null;
  return (
    <div
      style={{
        marginTop: 14,
        display: "flex",
        height: 12,
        border: "1px solid var(--border)",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {timeline.map((seg, i) => (
        <div
          key={i}
          style={{
            width: `${(seg.durationMs / totalMs) * 100}%`,
            background: seg.on ? "var(--accent)" : "transparent",
          }}
        />
      ))}
    </div>
  );
}
